import * as React from "react";
import { AiChatService, type AiChatRequestContentResponse } from "../../../../../../services/ai-chat-service";
import type { AIWorkspaceRequestRow } from "../../../../../../services/ai-payment-service";
import KuikaCopyButton from "../../../../../ui-kit/kuika-copy-button/kuika-copy-button";
import { KuikaSkeletonDetail, KuikaSkeletonRegion } from "../../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import { formatCreditAmount } from "../../ai-chatbot-utils";

// ── Request detail drawer (KOA-18066) ───────────────────────────────────────
// Right-side <aside role="dialog"> showing one chat turn's metadata (from the
// already-loaded Backend row) + prompt/response content lazily fetched from
// the mcp-chat-server's telemetry. Content is best-effort: unavailable rows
// (other instance / pruned / pre-feature) render a friendly empty state.

interface AnalyticsRequestDrawerProps {
  request: AIWorkspaceRequestRow;
  onClose: () => void;
}

interface State {
  loading: boolean;
  content: AiChatRequestContentResponse | null;
}

class AnalyticsRequestDrawer extends React.PureComponent<AnalyticsRequestDrawerProps, State> {
  state: State = { loading: true, content: null };

  private abortController: AbortController | null = null;

  private previousFocus: HTMLElement | null = null;

  async componentDidMount() {
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.addEventListener("keydown", this.handleKeyDown);

    const requestId: string | null = this.props.request.requestId;
    if (!requestId) {
      this.setState({ loading: false, content: null });
      return;
    }
    this.abortController = new AbortController();
    try {
      const content: AiChatRequestContentResponse | null = await AiChatService.GetRequestContent(
        requestId,
        this.abortController.signal
      );
      if (this.abortController.signal.aborted) return;
      this.setState({ loading: false, content });
    } catch {
      // Cancelled — the drawer is closing.
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.abortController?.abort();
    this.previousFocus?.focus?.();
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.props.onClose();
  };

  private renderMeta() {
    const { request } = this.props;
    const when: string = new Date(request.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    const items: { label: string; value: string }[] = [
      { label: "When", value: when },
      { label: "Member", value: request.userEmail ?? "—" },
      { label: "Project", value: request.projectName ?? "Unattributed" },
      { label: "Model", value: request.model ?? "—" },
      { label: "Credits", value: formatCreditAmount(request.creditsConsumed) },
      {
        label: "Tokens",
        value: `${request.tokensInput.toLocaleString("en-US")} in · ${request.tokensOutput.toLocaleString("en-US")} out`
      }
    ];
    return (
      <div className="aichat-analytics__drawer-meta">
        {items.map((item) => (
          <div key={item.label} className="aichat-analytics__drawer-meta-item">
            <span className="aichat-analytics__drawer-meta-label">{item.label}</span>
            <span className="aichat-analytics__drawer-meta-value">{item.value}</span>
          </div>
        ))}
      </div>
    );
  }

  private renderTextBlock(title: string, text: string | null) {
    if (text === null || text.trim().length === 0) return null;
    return (
      <div className="aichat-analytics__drawer-section">
        <div className="aichat-analytics__drawer-section-head">
          <span>{title}</span>
          {/* The shared copy control: it only says "copied" when the clipboard took the text. */}
          <KuikaCopyButton value={text} type="tertiary" color="neutral" label={`Copy ${title.toLowerCase()}`} />
        </div>
        <pre className="aichat-analytics__code">{text}</pre>
      </div>
    );
  }

  private renderContent() {
    const { loading, content } = this.state;
    if (loading) {
      // The content's shape inside the drawer, not a full-screen spinner over the whole designer (L7).
      return (
        <KuikaSkeletonRegion label="Loading request content">
          <KuikaSkeletonDetail rows={4} />
        </KuikaSkeletonRegion>
      );
    }
    if (!content || !content.available) {
      return (
        <div className="aichat-analytics__drawer-unavailable">
          <KuikaSvg name="info-circle" width={20} height={20} />
          <div>
            <div className="aichat-analytics__drawer-unavailable-title">Message content isn't available</div>
            <div className="aichat-analytics__drawer-unavailable-body">
              Chat content is kept on the AI server for recent requests only — this one is older or was served by
              another instance. Usage numbers above are always complete.
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        {this.renderTextBlock("Prompt", content.promptText)}
        {this.renderTextBlock("Response", content.responseText)}
      </>
    );
  }

  render() {
    return (
      <div className="aichat-analytics__drawer-root" data-testid="aiChatbotRequestDrawer">
        <div className="aichat-analytics__drawer-backdrop" onClick={this.props.onClose} aria-hidden="true" />
        <aside className="aichat-analytics__drawer" role="dialog" aria-modal="true" aria-label="Chat request detail">
          <div className="aichat-analytics__drawer-head">
            <span className="aichat-analytics__drawer-title">Request detail</span>
            <button
              type="button"
              className="aichat-analytics__drawer-close"
              onClick={this.props.onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          {this.renderMeta()}
          {this.renderContent()}
        </aside>
      </div>
    );
  }
}

export default AnalyticsRequestDrawer;
